/**
 * Tournament setup client (#2691). Thin wrappers over the setup routes; every
 * number, band, lock and sentence comes from the server.
 */
import { httpClient, isApiError } from "@bw/shared/api";
import { asMoney } from "@bw/shared/types";
import type { Money } from "@bw/shared/types";
import type { Schema } from "@bw/shared/types/generated";

export type SetupPreviewRequest = Schema["SetupPreviewRequest"];
export type SetupPreview = Schema["SetupPreview"];
export type TournamentSetupCreate = Schema["TournamentSetupCreate"];
export type TournamentSetupView = Schema["TournamentSetupView"];
export type SetupCommand =
  | Schema["UpdateBasics"]
  | Schema["SetDays"]
  | Schema["SetCourts"]
  | Schema["SetRegistrationWindow"]
  | Schema["SetCrossDrawLimits"]
  | Schema["SetTimeSettings"]
  | Schema["AddEvents"]
  | Schema["UpdateEvents"]
  | Schema["RemoveEvent"]
  | Schema["DuplicateEvent"]
  | Schema["ApplyChanges"]
  | Schema["OpenRegistration"];
export type SetupWhatIf = Schema["SetupWhatIf"];
export type SetupWhatIfResult = Schema["SetupWhatIfResult"];
export type TimeCheckResult = Schema["TimeCheckResult"];
export type TimeCheckDay = Schema["TimeCheckDay"];
export type TimeCheckSuggestion = Schema["TimeCheckSuggestion"];
export type RegistrationHint = Schema["RegistrationHint"];
export type SetupDay = Schema["SetupDay"];
export type StageDay = Schema["StageDay"];
export type MatrixCell = Schema["MatrixCell"];
export type SetupEventView = Schema["SetupEventView"];
export type SetupEventDraft = Schema["SetupEventDraft-Output"];
export type SetupEventDraftInput = Schema["SetupEventDraft-Input"];
export type EventPatch = Schema["EventPatch"];
export type EventRules = Schema["EventRules"];
export type FormatOption = Schema["FormatOption"];
export type FormatKey = Schema["FormatOption"]["format"];
export type QuickStart = Schema["SetupPreviewRequest"]["quick_start"];
export type TitledLine = Schema["TitledLine"];
export type ChecklistItem = Schema["ChecklistItem"];
export type TimeSettings = Schema["TimeSettings"];
export type CrossDrawLimits = Schema["CrossDrawLimits"];
export type CourtConflictView = Schema["CourtConflictView"];
export type Band = TimeCheckDay["band"];
export type LevelCode = NonNullable<MatrixCell["level"]>;
export type AgeGroupCode = NonNullable<MatrixCell["age_group"]>;
export type EventKind = MatrixCell["kind"];

export function previewTournamentSetup(facilityId: string, body: SetupPreviewRequest) {
  return httpClient.post<SetupPreview>(
    `/facilities/${encodeURIComponent(facilityId)}/tournament-setups/preview`,
    body,
  );
}

export function createTournamentSetup(facilityId: string, body: TournamentSetupCreate) {
  return httpClient.post<TournamentSetupView>(
    `/facilities/${encodeURIComponent(facilityId)}/tournament-setups`,
    body,
  );
}

export function getTournamentSetup(tournamentId: string) {
  return httpClient.get<TournamentSetupView>(
    `/tournaments/${encodeURIComponent(tournamentId)}/setup`,
  );
}

export function applySetupCommand(tournamentId: string, body: SetupCommand) {
  return httpClient.post<TournamentSetupView>(
    `/tournaments/${encodeURIComponent(tournamentId)}/setup/commands`,
    body,
  );
}

export function whatIfSetup(tournamentId: string, body: SetupWhatIf) {
  return httpClient.post<SetupWhatIfResult>(
    `/tournaments/${encodeURIComponent(tournamentId)}/setup/what-if`,
    body,
  );
}

/** A decimal fee string ("35.00") as the branded money type the API uses. */
export function money(value: string): Money {
  return asMoney(value) ?? (asMoney("0.00") as Money);
}

export const setupKeys = {
  view: (id: string) => ["tournament-setup", id] as const,
};

export function newIdempotencyKey(): string {
  return crypto.randomUUID();
}

/** The server's message for a setup error, or a plain fallback. */
export function setupErrorMessage(error: unknown, fallback = "Something went wrong. Try again."): string {
  if (isApiError(error)) return error.message || fallback;
  if (error instanceof Error && error.message) return error.message;
  return fallback;
}

export function setupErrorCode(error: unknown): string | null {
  return isApiError(error) ? error.code : null;
}
